import { useState, useCallback } from 'react';
import './Share.css';

interface ShareProps {
  code: string;
}

// Encode code to base64 for URL
function encodeCode(code: string): string {
  try {
    return btoa(encodeURIComponent(code));
  } catch {
    return btoa(unescape(encodeURIComponent(code)));
  }
}

// Decode code from base64 URL parameter
function decodeCode(encoded: string): string {
  try {
    return decodeURIComponent(atob(encoded));
  } catch {
    try {
      return decodeURIComponent(escape(atob(encoded)));
    } catch {
      return '';
    }
  }
}

// Get code from URL hash parameter
export function getCodeFromUrl(): string | null {
  const hash = window.location.hash;
  if (!hash) return null;

  const params = new URLSearchParams(hash.slice(1));
  const encoded = params.get('code');

  if (!encoded) return null;

  const decoded = decodeCode(encoded);
  return decoded || null;
}

// Clear code from URL without page reload
export function clearCodeFromUrl(): void {
  if (window.location.hash) {
    history.replaceState(null, '', window.location.pathname + window.location.search);
  }
}

function Share({ code }: ShareProps) {
  const [showToast, setShowToast] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleShare = useCallback(async () => {
    // Create shareable URL
    const encoded = encodeCode(code);
    const url = `${window.location.origin}${window.location.pathname}#code=${encoded}`;

    // Copy to clipboard
    try {
      await navigator.clipboard.writeText(url);

      // Show toast
      setShowToast(true);
      setIsAnimating(true);

      // Hide toast after delay
      setTimeout(() => {
        setIsAnimating(false);
        setTimeout(() => setShowToast(false), 300);
      }, 2000);
    } catch (err) {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);

      setShowToast(true);
      setIsAnimating(true);
      setTimeout(() => {
        setIsAnimating(false);
        setTimeout(() => setShowToast(false), 300);
      }, 2000);
    }
  }, [code]);

  return (
    <div className="share">
      <button
        className="share-button"
        onClick={handleShare}
        title="Partager le code"
        aria-label="Partager le code"
      >
        <svg
          className="share-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="18" cy="5" r="3" />
          <circle cx="6" cy="12" r="3" />
          <circle cx="18" cy="19" r="3" />
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
        </svg>
        <span className="share-text">Partager</span>
      </button>

      {/* Toast Notification */}
      {showToast && (
        <div className={`share-toast ${isAnimating ? 'visible' : ''}`}>
          <span className="toast-icon">&#10003;</span>
          Lien copie!
        </div>
      )}
    </div>
  );
}

export default Share;
